// src/components/CoachChat.js
//
// Coach tab: engine proposals (accept / reject), chat with the LLM coach,
// and the coach's own proposals — all server-backed, so the thread and
// every decision survive devices and deploys.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { History, Mic, X, Check } from "lucide-react";
import { api } from "../api";
import CoachHistorySheet from "./CoachHistorySheet";
import { renderCoachText } from "./coachText";
import { useVoiceInput, VOICE_MAX_SECONDS } from "./useVoiceInput";

/* A day as the athlete reads it: "Sun 18 Oct". */
const cardDay = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : String(iso || "");
};
const cardType = (t) => {
  const s = String(t || "").replace(/-/g, " ");
  const l = s === "b2b day1" ? "long, B2B day 1" : s === "b2b day2" ? "long, B2B day 2" : s;
  return l.charAt(0).toUpperCase() + l.slice(1);
};
const km1 = (n) => (Number.isFinite(Number(n)) ? String(Math.round(Number(n) * 10) / 10) : "—");

/* What the proposal does, as rows: every session it touches with its exact
   before and after, then the week totals. The figures come from the server's
   dry run (proposal.preview) — the same ones the acceptance message quotes.
   Older proposals carry only the operations; those keep the short chips. */
function ProposalPreview({ preview }) {
  const sessions = (preview && preview.sessions) || [];
  const weeks = (preview && preview.weeks) || [];
  if (!sessions.length && !weeks.length) return null;
  const shown = sessions.slice(0, 8);
  const more = sessions.length - shown.length;
  return (
    <div className="coachDiff">
      {shown.map((s, i) => {
        const up = !s.skipped && !s.toDate && s.toKm > s.fromKm;
        const down = s.skipped || (!s.toDate && s.toKm < s.fromKm);
        return (
          <div key={i} className={"coachDiff__row" + (up ? " is-up" : down ? " is-down" : "")}>
            <span className="coachDiff__when">{cardDay(s.date)}</span>
            <span className="coachDiff__what">{s.fromType && s.fromType !== s.type ? `${cardType(s.fromType)} → ${cardType(s.type).toLowerCase()}` : cardType(s.type)}</span>
            <span className="coachDiff__val">
              {s.skipped ? <><s>{km1(s.fromKm)} km</s><b>skipped</b></>
                : s.toDate ? <b>to {cardDay(s.toDate)}</b>
                : s.fromMin != null ? <><i>{s.fromMin}</i><em aria-hidden="true">→</em><b>{s.toMin} min</b></>
                : Number(s.fromKm) === Number(s.toKm) ? <b>{km1(s.toKm)} km</b>
                : <><i>{km1(s.fromKm)}</i><em aria-hidden="true">→</em><b>{km1(s.toKm)} km</b></>}
            </span>
          </div>
        );
      })}
      {more > 0 ? <div className="coachDiff__more">and {more} more {more === 1 ? "session" : "sessions"}</div> : null}
      {weeks.map((w) => (
        <div key={`w${w.weekNumber}`} className={"coachDiff__row coachDiff__row--week" + (w.toKm > w.fromKm ? " is-up" : " is-down")}>
          <span className="coachDiff__when">Week {w.weekNumber}</span>
          <span className="coachDiff__what">Total</span>
          <span className="coachDiff__val"><i>{km1(w.fromKm)}</i><em aria-hidden="true">→</em><b>{km1(w.toKm)} km</b></span>
        </div>
      ))}
      {(preview.notes || []).map((n, i) => <div key={`n${i}`} className="coachDiff__note">{n}</div>)}
    </div>
  );
}

function ProposalCard({ adj, onDecide, busy, resolved, failure }) {
  const p = adj.proposal || {};
  const changes = p.changes || [];
  const hasPreview = !!(p.preview && ((p.preview.sessions || []).length || (p.preview.weeks || []).length));
  const risks = Array.isArray(p.risks) ? p.risks : [];
  const mine = !!p.athleteRequested;
  return (
    <div className={`coachCard coachCard--${p.action || "reduce_volume"}${mine ? " coachCard--request" : ""}${resolved ? " is-resolved" : ""}`}>
      <div className="coachCard__head">
        <span className="coachCard__label">{adj.source === "engine" ? "ENGINE PROPOSAL" : mine ? "YOUR REQUEST" : "COACH PROPOSAL"}</span>
        {p.reason && p.reason.methodologyRef && <span className="coachCard__rule">{p.reason.methodologyRef}</span>}
      </div>
      <div className="coachCard__headline">{p.headline}</div>
      {p.detail ? <div className="coachCard__detail">{p.detail}</div> : null}
      {hasPreview ? <ProposalPreview preview={p.preview} /> : changes.length > 0 && (
        <div className="coachCard__weeks">
          {changes.map((c, i) => (
            <span key={i} className="coachCard__weekChip">
              {c.kind === "scale_week" && `Week ${c.weekNumber}: ${c.deltaPct > 0 ? "+" : ""}${c.deltaPct}%${c.demoteQuality ? ", quality → easy" : ""}`}
              {c.kind === "set_session" && `${cardDay(c.date)}: ${km1(c.plannedKm)} km`}
              {c.kind === "swap_session" && `${cardDay(c.date)}: → ${c.toType}`}
              {c.kind === "move_session" && `${cardDay(c.date)} → ${cardDay(c.toDate)}`}
              {c.kind === "skip_session" && `${cardDay(c.date)}: skip`}
            </span>
          ))}
        </div>
      )}
      {/* What it costs, from the athlete's own numbers. Stated, never a
          reason to withhold the buttons: the decision is theirs. */}
      {risks.length > 0 && adj.status === "proposed" ? (
        <div className="coachCost">
          <div className="coachCost__label">What it costs</div>
          {risks.map((r, i) => <div key={i} className="coachCost__line">{r}</div>)}
        </div>
      ) : null}
      {failure ? <div className="coachCard__error" role="alert">{failure}</div> : null}
      {adj.status === "proposed" ? (
        <div className="coachCard__actions">
          <button type="button" className="btn btnOk" onClick={() => onDecide(adj, "accept")} disabled={busy}>{mine && risks.length ? "Do it anyway" : "Accept"}</button>
          <button type="button" className="btn btnGhost" onClick={() => onDecide(adj, "reject")} disabled={busy}>Not now</button>
        </div>
      ) : (
        <div className="coachCard__foot">
          {adj.status === "accepted" ? "Applied to your plan." : adj.source === "engine" ? "Declined — the coach won’t raise this again this week." : "Declined. Ask again any time."}
        </div>
      )}
    </div>
  );
}

const PHASE_LINE = {
  base: "Base. Aerobic volume is the job — easy running, consistently.",
  build: "Build. The quality sessions are what this block is for.",
  specific: "Specific. Race-like terrain and effort from here.",
  taper: "Taper. Less volume, same sharpness. Hold the paces, cut the work.",
};

export default function CoachChat({ hasPlan, goalLabel, onPlanChanged, refreshKey, weekContext }) {
  const [messages, setMessages] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [enabled, setEnabled] = useState(true);
  // Voice input exists only where the server can transcribe (Workers AI).
  const [voiceOn, setVoiceOn] = useState(false);
  const [error, setError] = useState("");
  const [engineNote, setEngineNote] = useState(null);
  const [reveal, setReveal] = useState(null);
  /* A proposal you just answered stays on screen for a beat showing the
     outcome, instead of vanishing the instant the list is re-filtered. */
  const [justDecided, setJustDecided] = useState(null);
  // A failed decision is reported ON the card, not above the composer.
  const [cardError, setCardError] = useState(null);
  /* The old conversation is not what this screen is for.
     Coach exists to tell you what to do about THIS week. It was opening on
     a wall of previous chat with the recommendation somewhere above it, so
     the first thing you had to do was scroll past weeks of your own
     questions to find out what the coach was actually proposing. The history
     is kept — it is behind one tap, and it is not in the way. */
  const priorIds = useRef(null);
  /* History lives in a sheet behind the History button in the composer, not
     in the thread: the thread is this visit. */
  const [historyOpen, setHistoryOpen] = useState(false);
  const inputRef = useRef(null);
  const historyBtnRef = useRef(null);
  const endRef = useRef(null);

  /* The thread is NEVER auto-scrolled by a render. It is scrolled by events:
     you sent something, or a reply arrived.
     The first attempt at this gated the scroll effects behind a "have we
     hydrated yet" ref, and it did not work — both effects also run on MOUNT,
     when messages is still []. That first run consumed the gate, and the
     moment the real thread loaded the second run scrolled to the bottom. You
     opened Coach from a proposal and landed at the end of an old
     conversation with the proposal off-screen above you.
     Effects that fire on mount cannot distinguish "arrived" from "was always
     there". Call sites can. */
  /* Keyboard-aware composer.
     iOS Safari does not resize the layout viewport when the keyboard opens —
     it OVERLAYS it. So a composer positioned against the bottom of the page
     ends up underneath the keyboard, Safari scrolls the whole document to
     drag the focused input into view, and the result is the composer and the
     thread lurching around on every focus and blur.
     VisualViewport reports what is actually visible. The keyboard's height is
     published as a CSS variable and the composer is pinned above it, so it
     never moves relative to the keyboard and the document never has to
     scroll to reveal it. The 80px floor keeps the URL bar collapsing from
     being mistaken for a keyboard. */
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    const root = document.documentElement;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const inset = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
      root.style.setProperty("--kb-inset", `${inset}px`);
      const open = inset > 80;
      if (document.body.classList.contains("kb-open") !== open) {
        document.body.classList.toggle("kb-open", open);
        if (open) scrollToEnd();   // keep the newest message off the keyboard
      }
    };
    const onChange = () => { if (!raf) raf = requestAnimationFrame(apply); };
    apply();
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    return () => {
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
      if (raf) cancelAnimationFrame(raf);
      root.style.removeProperty("--kb-inset");
      document.body.classList.remove("kb-open");
      document.body.classList.remove("coach-typing");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToEnd = () => {
    requestAnimationFrame(() => {
      if (endRef.current) endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
    });
  };

  const load = async () => {
    try {
      const [m, a] = await Promise.all([api.get("/api/coach/messages"), api.get("/api/adjustments")]);
      /* Everything that already existed when this screen opened is HISTORY.
         Recorded once, by id: the screen reloads after a decision and the
         server's list grows, so an index or a count would drift. */
      if (!priorIds.current) priorIds.current = new Set((m.messages || []).map((x) => x.id));
      setMessages(m.messages || []);
      setEnabled(m.enabled !== false);
      setVoiceOn(m.voice === true);
      setAdjustments(a.adjustments || []);
    } catch (_) {}
  };

  const evaluate = async () => {
    if (!hasPlan) return;
    try {
      const r = await api.post("/api/adaptation/evaluate", {});
      setEngineNote(r.proposal || null);
      if (r.adjustment) setAdjustments((prev) => (prev.some((x) => x.id === r.adjustment.id) ? prev : [r.adjustment, ...prev]));
    } catch (_) {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); evaluate(); }, [refreshKey, hasPlan]);
  useEffect(() => {
    if (!reveal) return;
    if (reveal.i >= reveal.chunks.length) { const t = setTimeout(() => setReveal(null), 220); return () => clearTimeout(t); }
    const t = setTimeout(() => {
      setReveal((r) => (r ? Object.assign({}, r, { i: r.i + (r.stride || 1) }) : null));
      scrollToEnd();
    }, 38);
    return () => clearTimeout(t);
  }, [reveal]);
  /* The answer arrives whole from the API and is then written out word by
     word. It used to appear a SENTENCE at a time, and only when there was
     more than one — so a short answer landed as a block with no sense of
     anything happening, and a long one arrived in slabs.
     Stride scales with length so a long answer is not slower to read than a
     short one: never more than ~70 ticks, about two and a half seconds,
     whatever the length. */
  const startReveal = (reply) => {
    const words = String(reply.content || "").split(/\s+/).filter(Boolean);
    if (words.length < 2) return;
    const stride = Math.max(2, Math.ceil(words.length / 70));
    setReveal({ id: reply.id, chunks: words, i: stride, stride });
  };

  const send = async (text) => {
    const message = (text || draft).trim();
    if (!message || sending) return;
    setSending(true);
    setError("");
    if (!text) setDraft("");   // a chip or 'Ask again' leaves the athlete's own draft alone
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: message, createdAt: Date.now() };
    setMessages((prev) => [...prev, optimistic]);
    scrollToEnd();                     // your own message, as you send it
    try {
      const r = await api.post("/api/coach/chat", { message });
      setMessages((prev) => [...prev, r.reply]);
      scrollToEnd();                   // the reply, as it arrives
      startReveal(r.reply);
      if (r.adjustment) setAdjustments((prev) => [r.adjustment, ...prev]);
    } catch (err) {
      setError(err.message || "Coach unavailable.");
    } finally {
      setSending(false);
    }
  };

  /* Talk instead of type. The words land in the composer — after whatever
     is already there — and are sent by the athlete, never by the app. */
  const voice = useVoiceInput({
    onText: (said) => setDraft((d) => { const cur = String(d || "").trim(); return cur ? `${cur} ${said}` : said; }),
  });
  const micShown = voiceOn && voice.supported;
  const recording = voice.state === "recording";
  const voiceBusy = voice.state === "requesting" || voice.state === "transcribing";
  const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const decide = async (adj, action) => {
    setDeciding(true);
    setError("");
    setCardError(null);
    try {
      const r = await api.post(`/api/adjustments/${adj.id}/${action}`, {});
      setAdjustments((prev) => prev.map((x) => (x.id === adj.id ? Object.assign({}, x, { status: action === "accept" ? (r.ok ? "accepted" : "rejected") : "rejected" }) : x)));
      if (action === "accept" && r.ok && r.plan) onPlanChanged && onPlanChanged(r.plan, r.preview || (adj.proposal && adj.proposal.preview) || null);
      if (action === "accept" && !r.ok) setError(r.message || "Nothing could be applied.");
      setJustDecided({ id: adj.id, at: Date.now() });
      await load();
    } catch (err) {
      /* 404 here means "not found, or already decided", and ALREADY DECIDED
         is a normal state, not a failure: the duplicate sweep closes sibling
         proposals, so a card on screen can name a row the server has since
         marked superseded. Treating that as an error left the card sitting
         there with its buttons — the athlete taps "Not now", nothing happens,
         and the only clue is an error line at the far bottom of the page,
         past the whole thread. It is resolved, so close it like one. */
      if (err.status === 404) {
        // Mark it closed locally as well as reloading. The reload is the
        // source of truth, but the card must clear even if the list is
        // momentarily stale — the whole complaint is a card that will not go.
        setAdjustments((prev) => prev.map((x) => (x.id === adj.id ? Object.assign({}, x, { status: "superseded" }) : x)));
        setJustDecided({ id: adj.id, at: Date.now() });
        await load();
      } else {
        /* Anything else IS a failure, and it belongs on the card that was
           tapped rather than above the composer two screens away. */
        setCardError({ id: adj.id, message: err.message || "Could not update the proposal." });
      }
    } finally {
      setDeciding(false);
    }
  };

  // Clear the lingering card once its outcome has been read.
  useEffect(() => {
    if (!justDecided) return undefined;
    const t = setTimeout(() => setJustDecided(null), 2400);
    return () => clearTimeout(t);
  }, [justDecided]);

  /* One card per question. The old evaluate() inserted a fresh engine row on
     every mount of this tab, so an account that ran with that bug is carrying
     a stack of identical open proposals — declining one just revealed the
     next and the card appeared never to go away. The server closes the
     duplicates when you decide (closeDuplicateAdjustments); this makes sure
     they are never SHOWN twice in the first place, whatever is in the table.
     Newest wins: adjustments arrive newest-first. */
  /* A proposal is about one training week; once that week is behind you
     there is nothing left to act on. The server expires these on read
     (PROPOSAL_TTL_MS), and this is the same rule applied locally so a stale
     card is never shown even for the moment before that lands — the whole
     complaint was decisions being offered about weeks long since run. */
  const STALE_MS = 10 * 24 * 60 * 60 * 1000;
  const seenRule = new Set();
  const open = adjustments.filter((a) => {
    if (justDecided && a.id === justDecided.id) return true;
    if (a.status !== "proposed") return false;
    if (a.createdAt && Date.now() - a.createdAt > STALE_MS) return false;
    const key = `${a.source}|${(a.proposal && a.proposal.reason && a.proposal.reason.rule) || a.id}`;
    if (seenRule.has(key)) return false;
    seenRule.add(key);
    return true;
  });
  /* The model was told plain text, but a model that slips will not be the
     reason an answer reads as "**9.8 km**" on screen. Bold is rendered,
     everything else is left alone, and it is done by SPLITTING the string —
     never by setting innerHTML — so nothing a model emits can become markup.
     Line breaks survive via white-space in CSS. */
  const renderText = renderCoachText;

  const renderMsg = (m) => (
    <div key={m.id} className={`coachMsg coachMsg--${m.role}`}>
      {m.role !== "system" && <div className="coachMsg__who">{m.role === "user" ? "You" : "Coach"}</div>}
      <div className="coachMsg__body">
        {reveal && reveal.id === m.id
          ? (<>{renderText(reveal.chunks.slice(0, reveal.i).join(" "))}<span className="coachCursor" /></>)
          : renderText(String(m.content || ""))}
      </div>
    </div>
  );
  // Anything present when the screen opened is history; the rest is this visit.
  const isPrior = (m) => !!priorIds.current && priorIds.current.has(m.id);
  const current = messages.filter((m) => !isPrior(m));
  /* Opening History blurs the composer first: on iOS a button in the fixed
     composer does not take focus, so the keyboard would otherwise stay up
     under the sheet. The draft is state and survives. */
  const openHistory = () => { if (inputRef.current) inputRef.current.blur(); setHistoryOpen(true); };
  const closeHistory = () => {
    setHistoryOpen(false);
    requestAnimationFrame(() => { if (historyBtnRef.current) { try { historyBtnRef.current.focus({ preventScroll: true }); } catch (_) {} } });
  };

  const wc = weekContext || { signals: [] };
  const chips = [
    { label: "Explain this week", q: "Explain this training week to me: phase, purpose, what matters most." },
    { label: `Am I ready for the ${goalLabel || "race"}?`, q: `Am I on track for my ${goalLabel || "goal race"}? Be honest and cite my numbers.` },
    { label: "I feel tired", q: "I feel tired and my legs are heavy. What should I change this week?" },
    { label: "Move my long run", q: "I cannot run my next long run on the planned day. What are my options?" },
  ];

  return (
    <div className="tabPage coachTab">
      {open.length > 0 ? (
        open.map((a) => (
          <ProposalCard
            key={a.id}
            adj={a}
            onDecide={decide}
            busy={deciding}
            resolved={!!justDecided && justDecided.id === a.id && a.status !== "proposed"}
            failure={cardError && cardError.id === a.id ? cardError.message : null}
          />
        ))
      ) : (
        /* Nothing to change this week is a RESULT, and it needs the same
           treatment as a proposal: say it, then show the reading behind it.
           This screen used to answer a tap on "Coach" with a status pill of
           engine-speak, four chips and a paragraph of marketing — no numbers,
           nothing about this week, and no reason to believe any of it. */
        <div className="coachRead">
          <div className="coachRead__eyebrow">
            {wc.weekNumber ? `Week ${wc.weekNumber}${wc.totalWeeks ? ` of ${wc.totalWeeks}` : ""}` : "This week"}
            {wc.phase ? <><span className="volChart__dotSep" />{wc.phase[0].toUpperCase() + wc.phase.slice(1)}</> : null}
          </div>
          <h2 className="coachRead__verdict">
            {!hasPlan
              ? "Set a goal and build a plan to bring your coach online."
              : engineNote && engineNote.action === "hold" && engineNote.headline
                ? engineNote.headline
                : "Nothing needs changing this week."}
          </h2>
          {hasPlan && wc.phase && PHASE_LINE[wc.phase] ? (
            <p className="coachRead__sub">{PHASE_LINE[wc.phase]}</p>
          ) : null}

          {wc.signals.length > 0 && (
            <div className="coachSignals">
              <div className="coachSignals__title">What the coach is reading</div>
              {wc.signals.map((sig) => (
                <div key={sig.key} className={`coachSignal coachSignal--${sig.tone}`}>
                  <span className="coachSignal__label">{sig.label}</span>
                  <span className="coachSignal__value">{sig.value}</span>
                  <span className="coachSignal__note">{sig.note}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!enabled && (
        <div className="muted" style={{ fontSize: "var(--text-xs-size)", margin: "6px 0 10px" }}>
          Coach chat runs in preview mode on this server (no model key configured).
        </div>
      )}

      <div className="coachChips">
        {chips.map((c) => (
          <button key={c.label} type="button" className="coachChip" onClick={() => send(c.q)} disabled={sending}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="coachThread">

        {current.map((m) => renderMsg(m))}
        {sending && (
          <div className="coachMsg coachMsg--assistant coachMsg--thinking">
            <div className="coachMsg__who">Coach</div>
            <div className="coachMsg__body">
              <span className="coachTyping" aria-hidden="true"><i /><i /><i /></span>
              <span className="coachThinking__label">Reading your plan…</span>
              {/* Two shimmering lines standing in for the answer that is
                  coming, so the wait has the shape of the thing being
                  waited for rather than being a blank box. */}
              <span className="coachShimmer" aria-hidden="true"><i /><i /></span>
              <span className="srOnly">Coach is thinking</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* "Recent decisions" is gone. Accepting or declining already writes
          a system line into the thread ("Accepted: …"), so it was a second
          collapsed control listing what the first one already contained —
          two disclosures on a screen whose job is to answer one question. */}

      {error && <div className="deltaNeg" style={{ fontSize: "var(--text-xs-size)", marginBottom: 6 }}>{error}</div>}

      <div className={"coachInputRow" + (recording ? " is-recording" : "")}>
        {voice.error ? (
          <div className="coachVoiceNote" role="status" onClick={voice.clearError}>{voice.error}</div>
        ) : null}
        {recording ? (
          <>
            <button type="button" className="coachHistoryBtn coachVoiceCancel" aria-label="Discard the recording" onClick={voice.cancel}>
              <X size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>
            <div className="coachRec" role="status" aria-live="off" aria-label={`Recording, ${voice.seconds} seconds`}>
              <span className="coachRec__dot" aria-hidden="true" />
              <span className="coachRec__bars" aria-hidden="true">
                {voice.levels.map((l, i) => <i key={i} style={{ transform: `scaleY(${Math.max(0.14, l).toFixed(2)})` }} />)}
              </span>
              <span className="coachRec__label">Listening</span>
              <span className="coachRec__time">{clock(voice.seconds)}<em> / {clock(VOICE_MAX_SECONDS)}</em></span>
            </div>
            <button type="button" className="coachSend coachVoiceDone" aria-label="Done, write it out" onClick={voice.stop}>
              <Check size={18} strokeWidth={2} aria-hidden="true" />
            </button>
          </>
        ) : (
          <>
            {messages.length > 0 ? (
              <button
                type="button"
                ref={historyBtnRef}
                className="coachHistoryBtn"
                aria-label="History"
                aria-haspopup="dialog"
                aria-expanded={historyOpen}
                onClick={openHistory}
              >
                <History size={16} strokeWidth={1.75} aria-hidden="true" />
              </button>
            ) : null}
            <input
              ref={inputRef}
              className="coachInput"
              placeholder={voice.state === "transcribing" ? "Writing it out…" : voice.state === "requesting" ? "Waiting for the microphone…" : "Ask your coach…"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              /* The dock goes while you are typing. Hiding it off the keyboard
                 inset alone was not enough — the inset is a measurement and it
                 can be wrong (a hardware keyboard, a browser that reports late,
                 a split keyboard). Focus is not a measurement, it is a fact. */
              onFocus={() => { document.body.classList.add("coach-typing"); scrollToEnd(); }}
              onBlur={() => document.body.classList.remove("coach-typing")}
              /* Typing stays possible while the phone asks for the
                 microphone: an unanswered prompt must not lock the composer. */
              disabled={sending || voice.state === "transcribing"}
            />
            {/* An empty composer offers the microphone; one with words in it
                offers Send. One button in that place, never both. */}
            {micShown && !draft.trim() ? (
              <button
                type="button"
                className={"coachSend coachMic" + (voiceBusy ? " is-busy" : "")}
                aria-label={voice.state === "transcribing" ? "Writing it out" : "Speak your message"}
                onClick={() => { if (inputRef.current) inputRef.current.blur(); voice.start(); }}
                disabled={sending || voiceBusy}
              >
                {voiceBusy ? <span className="coachMic__spin" aria-hidden="true" /> : <Mic size={18} strokeWidth={1.75} aria-hidden="true" />}
              </button>
            ) : (
              <button type="button" className="coachSend" aria-label="Send" onClick={() => send()} disabled={sending || !draft.trim()}>↑</button>
            )}
          </>
        )}
      </div>
      {historyOpen && typeof document !== "undefined"
        ? createPortal(
            <CoachHistorySheet
              messages={messages}
              onClose={closeHistory}
              onAskAgain={(q) => { setHistoryOpen(false); send(q); }}
              sending={sending}
              capped={messages.length >= 60}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
